<template>
  <div class="fission-color-row">
    <div class="fcr-header">
      <span class="fcr-label">{{ color.label }}</span>
      <button class="fcr-remove" v-if="canRemove" @click="$emit('remove')" title="移除颜色">&times;</button>
    </div>
    <div class="fcr-body">
      <div class="fcr-uploads">
        <div class="fcr-ref-upload" @click="triggerUpload" :class="{ 'has-image': color.refPreview }">
          <template v-if="color.refPreview">
            <img :src="color.refPreview" class="fcr-ref-img" />
            <div class="fcr-ref-overlay">
              <span>替换</span>
            </div>
          </template>
          <template v-else>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            <span>颜色参考</span>
          </template>
          <input ref="fileInput" type="file" accept="image/*" class="hidden-input" @change="onFileChange" />
        </div>
        <div class="fcr-ref-upload fcr-detail-upload" @click="triggerDetailUpload" :class="{ 'has-image': color.detailPreview }">
          <template v-if="color.detailPreview">
            <img :src="color.detailPreview" class="fcr-ref-img" />
            <div class="fcr-ref-overlay">
              <span>替换</span>
            </div>
          </template>
          <template v-else>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <span>细节参考</span>
          </template>
          <input ref="detailFileInput" type="file" accept="image/*" class="hidden-input" @change="onDetailFileChange" />
        </div>
      </div>
      <div class="fcr-results">
        <div
          v-for="(result, ri) in color.results"
          :key="ri"
          class="fcr-result-slot"
          :class="{ selected: workbench.isFissionImageSelected(productIndex, colorIndex, ri) }"
        >
          <template v-if="result.loading">
            <div class="scg-loading-ring small">
              <svg viewBox="0 0 36 36">
                <circle class="ring-bg" cx="18" cy="18" r="16" fill="none" stroke="#e5e6eb" stroke-width="3"/>
                <circle class="ring-fg" cx="18" cy="18" r="16" fill="none" stroke="#d98a52" stroke-width="3"
                  :stroke-dasharray="100" :stroke-dashoffset="100 - result.progress" stroke-linecap="round"
                  transform="rotate(-90 18 18)"/>
              </svg>
              <span class="ring-text">{{ Math.round(result.progress) }}%</span>
            </div>
          </template>
          <template v-else-if="result.url">
            <img :src="result.url" class="fcr-result-img" @click="openFissionLightbox(result.url)" />
            <div class="fcr-check" @click.stop="workbench.toggleFissionImageSelection(productIndex, colorIndex, ri)">
              <svg v-if="workbench.isFissionImageSelected(productIndex, colorIndex, ri)" viewBox="0 0 24 24" fill="#d98a52" width="16" height="16">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" width="16" height="16">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
              </svg>
            </div>
            <div class="fcr-result-actions">
              <button class="fcr-action-btn" title="重新生成" @click.stop="workbench.generateOneFission(productIndex, colorIndex, ri)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
              </button>
              <button class="fcr-action-btn delete" title="删除" @click.stop="workbench.clearFissionResult(productIndex, colorIndex, ri)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </template>
          <template v-else>
            <div class="scg-empty-slot clickable" @click="workbench.generateOneFission(productIndex, colorIndex, ri)">
              <svg viewBox="0 0 24 24" fill="none" stroke="#c9cdd4" stroke-width="1.5" width="20" height="20">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const props = defineProps({
  productIndex: { type: Number, required: true },
  colorIndex: { type: Number, required: true },
  color: { type: Object, required: true },
  canRemove: { type: Boolean, default: true }
})

defineEmits(['remove'])
const workbench = useWorkbenchStore()
const fileInput = ref(null)
const detailFileInput = ref(null)

const triggerUpload = () => { fileInput.value.value = ''; fileInput.value.click() }
const triggerDetailUpload = () => { detailFileInput.value.value = ''; detailFileInput.value.click() }

const onFileChange = (e) => {
  const file = e.target.files?.[0]
  if (file) workbench.setFissionColorRef(props.productIndex, props.colorIndex, file)
}

const onDetailFileChange = (e) => {
  const file = e.target.files?.[0]
  if (file) workbench.setFissionColorDetail(props.productIndex, props.colorIndex, file)
}

const openFissionLightbox = (url) => {
  const color = workbench.imageGen.fissionColors[props.productIndex]?.[props.colorIndex]
  const urls = (color?.results || []).filter(r => r.url).map(r => r.url)
  workbench.openLightbox(url, urls)
}
</script>

<style scoped>
/* 勾选图标与进度环统一使用主题主色（模板属性已为焦糖色，此处用变量兜底保持一致） */
.fcr-check svg[fill="#d98a52"] {
  fill: var(--primary-color);
}
.ring-fg {
  stroke: var(--primary-color);
}
/* 覆写全局 components.css 中操作按钮的蓝色 hover（scoped 优先级更高） */
.fcr-action-btn:hover {
  background: var(--primary-color);
}
.fcr-action-btn.delete:hover {
  background: var(--danger);
}
</style>
